import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { query } from '@warp-drive/utilities/json-api';
import {
  deriveSlrLoanStandingAction,
  deriveSlrLoanStandingStatus,
  normalizeSlrGoodStanding,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  createEmployeePlanAccountDocumentRequest,
  confirmSlrLoanStandingNameMatchRequest,
} from 'tio-data/requests/slr-loan-standing-verification';
import type { Store } from '@warp-drive/core';
import { planRequiresLoanStandingVerification } from 'tio-employee/utils/slr-plan-eligibility';
import type SessionContextService from 'tio-employee/services/session-context';
import type ObservablesService from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type { UploadFile } from 'ember-file-upload';
import type { EmployeePlanAccount } from 'tio-data/data/resources/employee-plan-account.type';
import type {
  SlrGoodStandingReason,
  SlrLoanStandingAuditState,
  SlrLoanStandingCablePayload,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

// Order a problem EPA is surfaced when none are eligible — most blocking first (`processing` is client-only, never derived here, but listed for completeness).
const ATTENTION_PRIORITY: SlrLoanStandingStatus[] = [
  'ineligible',
  'cannot_determine',
  'try_again',
  'pending_upload',
  'unknown',
  'processing',
];

/** Upload lifecycle (Figma v3 767-7670): submitting (create chain) → processing (AI pipeline) → verifying (eligibility eval) → settled/timeout/error. */
export type SlrLiveUploadPhase =
  'idle' | 'submitting' | 'processing' | 'verifying' | 'settled' | 'timeout' | 'error';

export type SlrLiveUploadErrorKind = 'upload_failed' | 'invalid_document' | 'processing_failed';

/** A cross-servicer redirect (#9972): another EPA's verdict riding the same asset stream. */
export interface SlrRedirectedVerdict {
  employeePlanAccountId: string | null;
  financialInstitutionName: string | null;
  state: SlrLoanStandingAuditState;
}

export interface SlrLiveUpload {
  phase: SlrLiveUploadPhase;
  /** Original filename of the submitted upload — anchors every banner to the file. */
  filename: string | null;
  /** The settled verdict, presentation-derived — only on `phase: 'settled'`. */
  outcome: SlrLoanStandingAuditState | null;
  errorKind: SlrLiveUploadErrorKind | null;
  /** Other-account verdicts redirected from this upload (cross-servicer mismatch). */
  redirectedVerdicts: SlrRedirectedVerdict[];
}

const IDLE_LIVE_UPLOAD: SlrLiveUpload = {
  phase: 'idle',
  filename: null,
  outcome: null,
  errorKind: null,
  redirectedVerdicts: [],
};

const LIVE_UPLOAD_BUSY_PHASES: ReadonlySet<SlrLiveUploadPhase> = new Set([
  'submitting',
  'processing',
  'verifying',
]);

// The verdict rides the full Observatory pipeline, so the window is generous — the fallback refetches rather than declaring failure.
const VERDICT_TIMEOUT_MS = 60_000;

interface ObservableDocumentBatchStatus {
  recordAttributes?: { extractionState?: string };
  transition?: { event?: string };
}

export default class SlrLoanStandingVerificationService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare observables: ObservablesService;
  @service declare cable: CableService;

  /**
   * Whether loan-standing verification is on for this participant's plan —
   * derived from the plan's selected eligibility conditions, which ARE the
   * participation signal (senior ruling, 2026-08-03).
   *
   * Scope is deliberately PRESENTATION ONLY. This decides whether the
   * dashboard's verification chrome renders; it is not an access control and
   * must not grow into one. The EmployeePlanAccount is a shared source of
   * truth — tio-payment reads `epa.good_standing` in its employee validation
   * (app/core/services/employee_validation.py, gated by the
   * `check_employee_plan_account` company payment setting) — so gating what
   * the EPA serves on a presentation flag could withhold data that payment
   * validation depends on. Per-client enforcement, if it is ever wanted,
   * belongs in the payment domain alongside that existing setting, not here.
   *
   * Server-side alignment of the /slr-alerts read model with this same signal
   * is tracked in tuitionio/tio-api#10001.
   */
  get #verificationEnabled() {
    const conditions = this.sessionContext.currentEmployee?.plan?.eligibilityConditions?.map(
      (condition) => condition.name
    );
    return planRequiresLoanStandingVerification(conditions);
  }

  /**
   * Reads good-standing state scoped to `accountReceivingContributionsId` (#8714/#9428), deliberately NOT
   * `employee_plan_id` — the latter silently excluded every real EPA and the alert never fired (the reliability bug this replaced). Aggregates to any eligible EPA, else the most blocking one; null with no receiving account or no in-scope EPAs.
   */
  fetchCurrentState = async (options?: { reload?: boolean }) => {
    const receivingAccountId = this.sessionContext.person?.accountReceivingContributionsId;
    if (!receivingAccountId) return null;

    const { content } = (await this.store.request(
      query<EmployeePlanAccount>(
        'employee-plan-account',
        {
          'filter[account_id]': receivingAccountId,
          // account.observation feeds documentName (Review Name panel); good-standing-source-document.observable-asset feeds the filename callout (#10131: a relationship, not a flattened attribute).
          include: 'account,good-standing-source-document.observable-asset',
        },
        // reload bypasses the soft-cache window: the PATCH is SkipCache and never invalidates this query's cached document.
        options?.reload ? { reload: true } : {}
      )
    )) as { content: { data: EmployeePlanAccount[] } };

    const person = this.sessionContext.person;
    const employerProvidedName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;

    const state = aggregateAuditState(
      content.data.map((epa) =>
        toAuditState(epa, this.#verificationEnabled, { employerProvidedName })
      )
    );
    // A settled hard-ineligible verdict is fully told by the page banner once
    // the refreshed route state agrees — retire the strip here rather than only
    // in the card's render-time check (shouldShowSlrLoanStandingAttentionCard):
    // the two stores refresh on independent paths (per-asset settle vs. the
    // broad document-activity refresh) and the render-time AND can miss the
    // beat between them, leaving both surfaces up (#10405).
    if (
      state?.status === 'ineligible' &&
      this.liveUpload.phase === 'settled' &&
      this.liveUpload.outcome?.status === 'ineligible'
    ) {
      this.dismissLiveUpload();
    }
    return state;
  };

  /**
   * Two writes (tio-api#9814/#7640): the shared Observatory create, then a join row naming the EPA;
   * the backend evaluates and broadcasts the verdict on `ServiceResponseChannel` keyed by `assetUuid` — subscribe with it, falling back to `fetchCurrentState({ reload: true })` on timeout.
   */
  createVerificationDocument = async (
    employeePlanAccountId: string,
    uploadFile: UploadFile,
    options: { batch?: string } = {}
  ) => {
    const person = this.sessionContext.person;
    if (!person) {
      throw new Error(
        '[SLR loan standing] Cannot upload a verification document without a session person'
      );
    }

    const document = await this.observables.createObservableDocument(uploadFile, {
      person,
      batch: options.batch,
    });
    if (!document.id) {
      throw new Error('[SLR loan standing] Observable document saved without an id');
    }

    await this.store.request(
      createEmployeePlanAccountDocumentRequest(employeePlanAccountId, document.id)
    );

    return {
      documentId: document.id,
      assetUuid: document.observableAsset?.uuid ?? null,
    };
  };

  /**
   * PATCHes `name_match_confirmation` on the EPA (tio-api#8054); the response is rendered pre-eval, so
   * refetch (reload, bypassing the SkipCache PATCH's soft-cache) for the settled state, falling back to the raw (unmaterialized) PATCH response if the refetch comes back empty.
   */
  confirmNameMatch = async (employeePlanAccountId: string, confirmed: boolean) => {
    const { content } = (await this.store.request(
      confirmSlrLoanStandingNameMatchRequest(
        employeePlanAccountId,
        confirmed ? 'confirmed' : 'denied'
      )
    )) as { content: { data: { id: string | null; attributes: Record<string, unknown> } } };

    const settled = await this.fetchCurrentState({ reload: true });
    if (settled) return settled;

    const raw = content.data;
    return toAuditState(
      {
        id: raw.id,
        ...(raw.attributes as Partial<EmployeePlanAccount>),
      } as EmployeePlanAccount,
      this.#verificationEnabled
    );
  };

  // Live upload (Figma v3 767-7670): the SERVICE owns cable subscriptions, the verdict timeout, and tracked state — hoisted from the upload drawer so it survives the row's re-renders, one in-flight verification per session.

  @tracked liveUpload: SlrLiveUpload = IDLE_LIVE_UPLOAD;

  #batchSubscriptionParams: object | null = null;
  #verdictSubscriptionParams: object | null = null;
  #verdictTimer?: ReturnType<typeof setTimeout>;
  #submittedEpaId: string | null = null;
  #submittedDocumentId: string | null = null;
  #submittedAssetUuid: string | null = null;
  #onSettled: (() => void) | null = null;
  // The receiving account changed while this upload was in flight — its settled
  // outcome will describe the PREVIOUS selection, so it retires on landing.
  #retireOnSettle = false;

  get isLiveUploadBusy() {
    return LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase);
  }

  willDestroy() {
    super.willDestroy();
    this.#teardownRealtime();
  }

  /** Submits and tracks to a settled verdict; the promise resolves after the create chain, the verdict arrives later via `liveUpload` (cable or timeout refetch) — `onSettled` fires once it lands. */
  submitVerificationDocument = async (
    employeePlanAccountId: string,
    uploadFile: UploadFile,
    options: { verdictTimeoutMs?: number; onSettled?: () => void } = {}
  ) => {
    // One verification at a time — a concurrent submit would cross-wire the subscriptions.
    if (this.isLiveUploadBusy) return;

    this.#teardownRealtime();
    this.#onSettled = options.onSettled ?? null;
    this.#retireOnSettle = false;
    this.#submittedEpaId = employeePlanAccountId;
    const filename = uploadFile.file.name;
    this.liveUpload = {
      phase: 'submitting',
      filename,
      outcome: null,
      errorKind: null,
      redirectedVerdicts: [],
    };

    const batchId = crypto.randomUUID();
    this.#subscribeToBatchStatus(batchId);
    // Armed BEFORE the create chain, so a stall anywhere in it — the signed-URL
    // fetch, the PUT, the join-row POST, the WarpDrive save — lands in
    // #verdictTimeoutFallback: refetch authoritative state, and with
    // #submittedDocumentId unset fall to the truthful 'timeout' phase rather
    // than a false failure. The timer is a state-resolution step, never a
    // verdict (ai-analysis-watcher's pattern).
    this.#armVerdictTimeout(options.verdictTimeoutMs);

    try {
      const { documentId, assetUuid } = await this.createVerificationDocument(
        employeePlanAccountId,
        uploadFile,
        { batch: batchId }
      );
      // The batch channel can terminal-error or advance to 'verifying' mid-create — never resurrect an error or downgrade an early verifying.
      if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;
      this.#submittedDocumentId = documentId;
      this.#submittedAssetUuid = assetUuid;
      // The earliest broadcast is minutes away (post-reflection) — subscribing right after the create chain can't miss it; timeout refetch covers a lost broadcast.
      if (assetUuid) this.#subscribeToVerdict(assetUuid);
      if (this.liveUpload.phase === 'submitting') {
        this.liveUpload = { ...this.liveUpload, phase: 'processing' };
      }
      // Re-arm now the chain is done: the verdict window shouldn't be docked
      // the seconds the create chain spent.
      this.#armVerdictTimeout(options.verdictTimeoutMs);
    } catch (error) {
      console.error('[SLR loan standing] Failed to submit verification document', error);
      this.#teardownRealtime();
      // A batch-driven terminal error is more specific than the generic chain failure — keep the first verdict.
      if (this.liveUpload.phase !== 'error') {
        this.liveUpload = {
          phase: 'error',
          filename,
          outcome: null,
          errorKind: 'upload_failed',
          redirectedVerdicts: [],
        };
      }
    }
  };

  /** Clear a settled/timed-out/errored upload back to the idle row. */
  dismissLiveUpload = () => {
    this.#teardownRealtime();
    this.#retireOnSettle = false;
    this.liveUpload = IDLE_LIVE_UPLOAD;
  };

  /**
   * The strip's guidance is scoped to the receiving account selected at submit
   * time — kept across a switch, a "not for your selected contribution account"
   * outcome interpolates the NEW account's name and contradicts itself
   * (#10404). Settled/errored/timed-out rows retire immediately. An in-flight
   * verification keeps its progress/error tracking — a terminal error is about
   * the FILE and must still report — but its account-scoped settled outcome is
   * marked to retire on landing (see #applyOutcome), or the same contradiction
   * comes back a verdict later.
   */
  handleReceivingAccountChanged = () => {
    if (this.liveUpload.phase === 'idle') return;
    if (this.isLiveUploadBusy) {
      this.#retireOnSettle = true;
      return;
    }
    this.dismissLiveUpload();
  };

  #unsubscribeBatch = () => {
    if (!this.#batchSubscriptionParams) return;
    this.cable.unsubscribe(this.#batchSubscriptionParams);
    this.#batchSubscriptionParams = null;
  };

  #unsubscribeVerdict = () => {
    if (!this.#verdictSubscriptionParams) return;
    this.cable.unsubscribe(this.#verdictSubscriptionParams);
    this.#verdictSubscriptionParams = null;
  };

  #teardownRealtime = () => {
    this.#unsubscribeBatch();
    this.#unsubscribeVerdict();
    clearTimeout(this.#verdictTimer);
  };

  /** Document-pipeline progress on the batch channel (also watched by the statement-upload drawer): surfaces file-level failures early, flips to 'verifying' once reflection lands. */
  #subscribeToBatchStatus = (batchId: string) => {
    const channelParams = { channel: 'ObservableDocumentBatchStatusChannel', batch: batchId };
    this.#batchSubscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const message = data as ObservableDocumentBatchStatus;
        const extractionState = message.recordAttributes?.extractionState ?? '';
        const event = message.transition?.event ?? '';

        if (['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(extractionState)) {
          this.#teardownRealtime();
          this.liveUpload = {
            ...this.liveUpload,
            phase: 'error',
            errorKind:
              extractionState === 'ExtractionState.INVALID'
                ? 'invalid_document'
                : 'processing_failed',
          };
          return;
        }

        if (event === 'reflection_failed') {
          this.#teardownRealtime();
          this.liveUpload = { ...this.liveUpload, phase: 'error', errorKind: 'processing_failed' };
          return;
        }

        if (event === 'content_reflected' && LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) {
          // Document read; the verdict now rides ServiceResponseChannel (or the timeout fallback) — accept from any busy phase since this can land while still 'submitting'.
          this.liveUpload = { ...this.liveUpload, phase: 'verifying' };
        }
      },
    });
  };

  /** The raw verdict broadcast by Slr::VerificationOutcomeBroadcaster — presentation is derived client-side so the mapping lives in one place. */
  #subscribeToVerdict = (assetUuid: string) => {
    const channelParams = { channel: 'ServiceResponseChannel', request_id: assetUuid };
    this.#verdictSubscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.#handleVerdictPayload(data as SlrLoanStandingCablePayload);
      },
    });
  };

  #handleVerdictPayload = (payload: SlrLoanStandingCablePayload) => {
    if (
      payload.assetUuid &&
      this.#submittedAssetUuid &&
      payload.assetUuid !== this.#submittedAssetUuid
    ) {
      return;
    }

    const epaDiffers =
      !!payload.employeePlanAccountId &&
      !!this.#submittedEpaId &&
      payload.employeePlanAccountId !== this.#submittedEpaId;

    // A redirected verdict (payload.redirected, or an EPA mismatch for older payloads) belongs to a different account — stash it, never let it settle this card's own outcome.
    if (payload.redirected === true || epaDiffers) {
      this.#handleRedirectedVerdict(payload);
      return;
    }

    if (!payload.goodStanding) return;

    // Normalize, never cast — a malformed backend value must degrade to 'unknown' rather than derive an undefined status/translation key.
    const goodStanding = normalizeSlrGoodStanding(payload.goodStanding);
    const reason = (payload.goodStandingReason ?? null) as SlrGoodStandingReason | null;

    this.#applyOutcome({
      employeePlanAccountId: payload.employeePlanAccountId ?? this.#submittedEpaId,
      status: deriveSlrLoanStandingStatus(goodStanding, reason),
      action: deriveSlrLoanStandingAction(goodStanding, reason),
      primaryReason: payload.goodStandingReason ?? null,
      reasons: payload.goodStandingReasons,
      sourceDocumentId: payload.sourceDocumentId ?? this.#submittedDocumentId,
      sourceDocumentFilename: this.liveUpload.filename,
      featureEnabled: this.#verificationEnabled,
    });
  };

  #handleRedirectedVerdict = (payload: SlrLoanStandingCablePayload) => {
    if (!payload.goodStanding) return;

    const goodStanding = normalizeSlrGoodStanding(payload.goodStanding);
    const reason = (payload.goodStandingReason ?? null) as SlrGoodStandingReason | null;

    const verdict: SlrRedirectedVerdict = {
      employeePlanAccountId: payload.employeePlanAccountId ?? null,
      financialInstitutionName: payload.financialInstitutionName ?? null,
      state: {
        employeePlanAccountId: payload.employeePlanAccountId ?? null,
        status: deriveSlrLoanStandingStatus(goodStanding, reason),
        action: deriveSlrLoanStandingAction(goodStanding, reason),
        primaryReason: payload.goodStandingReason ?? null,
        reasons: payload.goodStandingReasons,
        sourceDocumentId: payload.sourceDocumentId ?? null,
        featureEnabled: this.#verificationEnabled,
      },
    };

    // Fire onSettled once for the first redirected verdict — a multi-account document can redirect more than one, only the first triggers the route refresh.
    const isFirstRedirect = this.liveUpload.redirectedVerdicts.length === 0;
    this.liveUpload = {
      ...this.liveUpload,
      redirectedVerdicts: [...this.liveUpload.redirectedVerdicts, verdict],
    };
    if (isFirstRedirect) this.#onSettled?.();
  };

  #applyOutcome = (state: SlrLoanStandingAuditState) => {
    // The receiving account changed mid-upload: this outcome describes the
    // previous selection, and rendering it would interpolate the new account's
    // name into guidance about the old one (#10404). Retire it — the settled
    // callback still fires so the route refreshes to the new selection's state.
    if (this.#retireOnSettle) {
      const onSettled = this.#onSettled;
      this.dismissLiveUpload();
      onSettled?.();
      return;
    }
    // The OTHER account's redirected verdict rides an independent job chain and can arrive after this settles — keep the verdict subscription alive for it.
    if (state.primaryReason === 'document_for_different_account') {
      this.#unsubscribeBatch();
      clearTimeout(this.#verdictTimer);
    } else {
      this.#teardownRealtime();
    }
    this.liveUpload = { ...this.liveUpload, phase: 'settled', outcome: state, errorKind: null };
    this.#onSettled?.();
  };

  /** A lost broadcast must never strand the row on "processing" (the TAS analysis watcher's lesson) — refetch after the window and show OUR verdict if it landed, else admit processing is still running. */
  #armVerdictTimeout = (verdictTimeoutMs?: number) => {
    clearTimeout(this.#verdictTimer);
    this.#verdictTimer = setTimeout(() => {
      void this.#verdictTimeoutFallback();
    }, verdictTimeoutMs ?? VERDICT_TIMEOUT_MS);
  };

  #verdictTimeoutFallback = async () => {
    if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;

    let settled: SlrLoanStandingAuditState | null = null;
    try {
      settled = await this.fetchCurrentState({ reload: true });
    } catch (error) {
      console.warn('[SLR loan standing] Timeout refetch failed', error);
    }

    // The cable verdict can land while the refetch is in flight and already settled the upload — a second outcome, or a 'timeout' overwrite, must not follow.
    if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;

    if (
      settled &&
      this.#submittedDocumentId &&
      settled.sourceDocumentId === this.#submittedDocumentId
    ) {
      this.#applyOutcome(settled);
      return;
    }

    this.#teardownRealtime();
    this.liveUpload = { ...this.liveUpload, phase: 'timeout' };
  };
}

// featureEnabled is an explicit parameter rather than an optional with a
// default: a missed call site should be a type error, not a gate that silently
// fails open (feature visible to a client who should not see it) or closed
// (feature hidden from one who should).
function toAuditState(
  epa: EmployeePlanAccount,
  featureEnabled: boolean,
  context?: { employerProvidedName?: string | null }
): SlrLoanStandingAuditState {
  const goodStanding = normalizeSlrGoodStanding(epa.goodStanding);
  const reason = (epa.goodStandingReason || null) as SlrGoodStandingReason | null;
  const observation = epa.account?.observation as Record<string, unknown> | undefined;
  // Observation-hash keys are dasherized on the wire (cf. account.ext's 'interest-weighted-average'); camel fallback covers demo fixtures.
  const documentName = observation?.['borrower-name'] ?? observation?.['borrowerName'];

  return {
    employeePlanAccountId: epa.id,
    status: deriveSlrLoanStandingStatus(goodStanding, reason),
    primaryReason: epa.goodStandingReason || null,
    action: deriveSlrLoanStandingAction(goodStanding, reason),
    // The live-upload timeout fallback matches this id against its submitted document to decide whether a refetched verdict is OURS or someone else's eval.
    sourceDocumentId: epa.goodStandingSourceDocument?.id ?? null,
    sourceDocumentFilename: epa.goodStandingSourceDocument?.observableAsset?.filename ?? null,
    featureEnabled,
    employerProvidedName: context?.employerProvidedName ?? null,
    documentName: typeof documentName === 'string' && documentName ? documentName : null,
    // Audit-window context (tio-api #9316/#9364) — empty for eligible accounts/no schedule, so deadline chips + period copy stay dark unless a window applies.
    auditRequiredBy: epa.auditRequiredBy ?? null,
    auditPastDue: epa.auditPastDue ?? false,
    contributionPeriodEnd: epa.contributionPeriodEnd ?? null,
    nextAuditStartsOn: epa.nextAuditStartsOn ?? null,
  };
}

function aggregateAuditState(
  states: SlrLoanStandingAuditState[]
): SlrLoanStandingAuditState | null {
  if (states.length === 0) return null;

  const eligible = states.find((state) => state.status === 'eligible');
  if (eligible) return eligible;

  for (const status of ATTENTION_PRIORITY) {
    const match = states.find((state) => state.status === status);
    if (match) return match;
  }

  return states[0] ?? null;
}
